import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Download, Zap, Globe, Bot, BarChart3, Users, Shield } from 'lucide-react';
import { siteData } from '../data/mock';

const Hero = () => {
  const { hero } = siteData;

  return (
    <section
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{ background: 'var(--gradient-hero)' }}
    >
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.5 }}
          className="absolute top-1/4 left-10 w-72 h-72 bg-[var(--accent-primary)]/20 rounded-full blur-3xl"
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.5, delay: 0.3 }}
          className="absolute bottom-1/4 right-10 w-96 h-96 bg-[var(--accent-strong)]/15 rounded-full blur-3xl"
        />
      </div>

      <div className="container relative z-10 pt-28 pb-16 md:pt-32 md:pb-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left - Text Content */}
          <div className="text-center lg:text-left">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/60 backdrop-blur-sm border border-black/10 mb-8"
            >
              <span className="w-2 h-2 rounded-full bg-[var(--accent-strong)] animate-pulse" />
              <span className="text-sm font-medium text-[var(--text-secondary)]">
                AI-First Transformation Agency
              </span>
            </motion.div>

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="heading-1 mb-6"
            >
              {hero.headline}
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="body-large max-w-xl mx-auto lg:mx-0 mb-10"
            >
              {hero.subheadline}
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4"
            >
              <a href="#contact" className="btn-primary w-full sm:w-auto">
                {hero.ctaText}
                <ArrowRight className="w-4 h-4" />
              </a>
              <a 
                href="https://customer-assets.emergentagent.com/job_aifirst-sme/artifacts/5m1nm9em_AI_First_Business_Transformation.pdf" 
                download="AI_First_Business_Transformation_Guide.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary w-full sm:w-auto"
              >
                <Download className="w-4 h-4" />
                {hero.ctaSecondary}
              </a>
            </motion.div>

            {/* Trust indicators */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-12 pt-8 border-t border-black/10"
          >
            <div className="flex items-center justify-center lg:justify-start gap-8 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-3xl font-bold text-[var(--accent-text)]">100+</span>
                <span className="text-sm text-[var(--text-muted)]">SMEs<br/>Transformed</span>
              </div>
              <div className="w-px h-10 bg-black/10 hidden sm:block" />
              <div className="flex items-center gap-2">
                <span className="text-3xl font-bold text-[var(--accent-text)]">20+</span>
                <span className="text-sm text-[var(--text-muted)]">Years<br/>Experience</span>
              </div>
              <div className="w-px h-10 bg-black/10 hidden sm:block" />
              <div className="flex items-center gap-2">
                <span className="text-3xl font-bold text-[var(--accent-text)]">UK</span>
                <span className="text-sm text-[var(--text-muted)]">Based<br/>Team</span>
              </div>
            </div>
          </motion.div>
          </div>

          {/* Right - Dashboard Visualization */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative hidden lg:block"
          >
            {/* Main Dashboard Card */}
            <div className="relative bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-black/10 p-6 transform rotate-2 hover:rotate-0 transition-transform duration-500">
              {/* Dashboard Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] flex items-center justify-center">
                    <Zap className="w-5 h-5 text-[var(--text-primary)]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[var(--text-primary)] text-sm">AI Command Centre</p>
                    <p className="text-xs text-[var(--text-muted)]">Real-time analytics</p>
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-[var(--accent-wash)] rounded-xl p-4 text-center">
                  <BarChart3 className="w-6 h-6 text-[var(--accent-text)] mx-auto mb-2" />
                  <p className="text-2xl font-bold text-[var(--text-primary)]">94%</p>
                  <p className="text-xs text-[var(--text-muted)]">Efficiency</p>
                </div>
                <div className="bg-[var(--accent-wash)] rounded-xl p-4 text-center">
                  <Users className="w-6 h-6 text-[var(--accent-text)] mx-auto mb-2" />
                  <p className="text-2xl font-bold text-[var(--text-primary)]">2.4k</p>
                  <p className="text-xs text-[var(--text-muted)]">Queries/day</p>
                </div>
                <div className="bg-[var(--accent-wash)] rounded-xl p-4 text-center">
                  <Shield className="w-6 h-6 text-[var(--accent-text)] mx-auto mb-2" />
                  <p className="text-2xl font-bold text-[var(--text-primary)]">99.9%</p>
                  <p className="text-xs text-[var(--text-muted)]">Uptime</p>
                </div>
              </div>

              {/* Connected Nodes Visualization */}
              <div className="bg-[var(--text-primary)] rounded-xl p-4 relative overflow-hidden">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-white/80 text-xs font-medium">Connected Systems</p>
                  <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">Live</span>
                </div>
                <div className="flex items-center justify-around">
                  {/* Node 1 */}
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 rounded-full bg-[var(--accent-primary)]/20 flex items-center justify-center border-2 border-[var(--accent-primary)]">
                      <Globe className="w-5 h-5 text-[var(--accent-primary)]" />
                    </div>
                    <p className="text-white/60 text-xs mt-2">Website</p>
                  </div>
                  {/* Connection Line */}
                  <div className="h-0.5 w-8 bg-gradient-to-r from-[var(--accent-primary)] to-[var(--accent-strong)]" />
                  {/* Node 2 - Center */}
                  <div className="flex flex-col items-center">
                    <div className="w-14 h-14 rounded-full bg-[var(--accent-primary)] flex items-center justify-center animate-pulse">
                      <Bot className="w-7 h-7 text-[var(--text-primary)]" />
                    </div>
                    <p className="text-white/60 text-xs mt-2">AI Core</p>
                  </div>
                  {/* Connection Line */}
                  <div className="h-0.5 w-8 bg-gradient-to-r from-[var(--accent-strong)] to-[var(--accent-primary)]" />
                  {/* Node 3 */}
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 rounded-full bg-[var(--accent-primary)]/20 flex items-center justify-center border-2 border-[var(--accent-primary)]">
                      <Users className="w-5 h-5 text-[var(--accent-primary)]" />
                    </div>
                    <p className="text-white/60 text-xs mt-2">CRM</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating notification card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1 }}
              className="absolute -bottom-4 -left-8 bg-white rounded-xl shadow-xl border border-black/10 p-4 transform -rotate-3"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <Bot className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[var(--text-primary)]">New lead captured</p>
                  <p className="text-xs text-[var(--text-muted)]">AI handled intake automatically</p>
                </div>
              </div>
            </motion.div>

            {/* Floating stat card */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1.2 }}
              className="absolute -top-4 -right-4 bg-white rounded-xl shadow-xl border border-black/10 p-4 transform rotate-3"
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[var(--accent-wash)] flex items-center justify-center">
                  <Zap className="w-4 h-4 text-[var(--accent-text)]" />
                </div>
                <div>
                  <p className="text-lg font-bold text-[var(--text-primary)]">+47%</p>
                  <p className="text-xs text-[var(--text-muted)]">Productivity</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <span className="text-xs text-[var(--text-muted)]">Scroll to explore</span>
        <div className="w-6 h-10 rounded-full border-2 border-black/20 flex justify-center pt-2">
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-1.5 h-3 rounded-full bg-[var(--text-secondary)]"
          />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
